import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { deleteAccount } from '../services';
import './DeleteAccount.css';

/**
 * What the screen says deleting does (Almanac S9b, decision 14). Every sentence
 * is a claim about `accounts.views.delete_account` on this tree, and each names
 * the backend behaviour test in `backend/accounts/tests.py`
 * (`DeleteAccountClaimTests`) that proves it. A claim without a passing test
 * there does not ship. Rendered verbatim, in order; the page test compares the
 * rendered lists with this constant. Mobile carries the same sentences in
 * `mobile/src/components/settings/DeleteAccountSection.tsx`.
 *
 * No counts: the deletion-preview endpoint (owner decision 3) was not built.
 */
export const DELETE_ACCOUNT_CLAIMS = {
  goes: [
    // test_claim_plans_and_calendar_connections_go
    'Your plans and your calendar connections.',
    // test_claim_explore_copies_are_taken_down_not_deleted
    'Their copies on Explore are taken down. They’re hidden, not deleted.',
    // test_claim_same_family_fork_goes_only_when_the_deleter_was_the_last_active_member
    // (subtest "last active member")
    'If you’re the last active member of your family, the family goes too, with its plans, including any your family made from yours.',
  ],
  stays: [
    // test_claim_forks_by_people_outside_the_family_stay_without_the_link, and
    // test_claim_same_family_fork_goes_only_when_the_deleter_was_the_last_active_member
    // (subtest "another active member")
    'Plans other people made from yours. They’re theirs now, without the link back to you, unless the family they’re in goes with you.',
    // test_claim_family_and_its_plans_stay_while_others_are_active_and_you_leave_it
    'If others in your family are still active, the family and its plans stay, and you leave it.',
    // test_claim_uploaded_photos_are_not_removed_from_storage (owner decision 7 not taken)
    'Photos you added to your plans. Deleting your account doesn’t remove them from storage.',
  ],
  // test_claim_store_billing_is_not_cancelled
  billing: 'Plus keeps billing after you delete your account. Cancel it first: in the App Store or Google Play if you bought it in the app, or under Settings → Plus on the web if you bought it on the web.',
} as const;

/**
 * What the screen says when the delete call rejects (S9b r942 finding 3,
 * revised per r943 finding 1). Every rejection is `unknown`. `delete_account`
 * has no 4xx of its own — it answers 200, or raises and rolls back to a 500 —
 * so a 4xx comes from authentication, CSRF, throttling or method, and a retry
 * after a committed delete whose response was lost gets exactly an
 * authentication 403. No rejection proves the account survived, so the screen
 * never says it did. The server's own message is never shown. Mobile carries
 * the same sentence in `mobile/src/components/settings/DeleteAccountSection.tsx`.
 */
export const DELETE_OUTCOME_COPY = {
  unknown: 'We couldn’t confirm whether your account was deleted. Sign in again to check — if it’s still there, you can retry.',
} as const;

/** The word the confirmation field must match exactly. */
export const DELETE_CONFIRM_WORD = 'DELETE';

const DeleteAccount: React.FC = () => {
  const navigate = useNavigate();
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (deleteConfirmText !== DELETE_CONFIRM_WORD) {
      setError(`Type ${DELETE_CONFIRM_WORD} to confirm.`);
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      await deleteAccount();
      window.location.href = '/login';
    } catch {
      setError(DELETE_OUTCOME_COPY.unknown);
      setIsDeleting(false);
    }
  };

  return (
    <div className="delete-account-page">
      <div className="delete-account-container">
        <button onClick={() => navigate('/settings')} className="back-link">
          ‹ Settings
        </button>

        <h1>Delete your account</h1>

        <h2 className="delete-account-label">What goes, for good</h2>
        <ul className="delete-account-claims" data-testid="delete-account-goes">
          {DELETE_ACCOUNT_CLAIMS.goes.map((claim) => <li key={claim}>{claim}</li>)}
        </ul>

        <h2 className="delete-account-label">What stays</h2>
        <ul className="delete-account-claims" data-testid="delete-account-stays">
          {DELETE_ACCOUNT_CLAIMS.stays.map((claim) => <li key={claim}>{claim}</li>)}
        </ul>

        <p className="delete-account-note" data-testid="delete-account-billing">
          {DELETE_ACCOUNT_CLAIMS.billing}
        </p>

        <div className="delete-confirm-section">
          <label className="confirm-instruction" htmlFor="delete-confirm-input">
            To confirm, type <strong>{DELETE_CONFIRM_WORD}</strong> below.
          </label>
          <input
            id="delete-confirm-input"
            type="text"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder={`Type ${DELETE_CONFIRM_WORD}`}
            className="delete-confirm-input"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
          />

          {error && (
            <p className="delete-error" role="alert">{error}</p>
          )}

          <div className="delete-buttons">
            <button
              onClick={handleDelete}
              disabled={isDeleting || deleteConfirmText !== DELETE_CONFIRM_WORD}
              className="btn-delete-confirm"
            >
              {isDeleting ? 'Deleting…' : 'Delete my account'}
            </button>
            <button
              onClick={() => navigate('/settings')}
              className="btn-cancel"
            >
              Keep it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccount;
